import { getElementFont, getTextMetrics } from '../../util/text-size.js';
import { getPeriodLabels, getMonthLabels, getDayLabels } from '../../../util/date-time-util.js';
import * as d3 from 'd3';
export class TimelineScale {
    timeZone;
    locale;
    tickTextStartMargin;
    tickTextEndMargin;
    _range;
    _domain;
    timelineScaleDefinitions;
    minorFontProperties;
    majorFontProperties;
    scaleReference;
    constructor(domain, range, timeZone = 'UTC', locale = 'en-GB', tickTextStartMargin = 0, tickTextEndMargin = 0, minorContainer, majorContainer) {
        this.timeZone = timeZone;
        this.locale = locale;
        this.tickTextStartMargin = tickTextStartMargin;
        this.tickTextEndMargin = tickTextEndMargin;
        this._domain =
            domain && domain.length > 1
                ? typeof domain[0] === 'object'
                    ? [domain[0].getTime(), domain[domain.length - 1].getTime()]
                    : [domain[0], domain[domain.length - 1]]
                : undefined;
        this._range = range && range.length > 1 ? [range[0], range[range.length - 1]] : undefined;
        this.minorFontProperties = minorContainer
            ? minorContainer.font
                ? minorContainer
                : getElementFont(minorContainer)
            : undefined;
        this.majorFontProperties = majorContainer
            ? majorContainer.font
                ? majorContainer
                : getElementFont(majorContainer)
            : undefined;
        this.refreshTimelineScaleDefinitions();
    }
    range(range) {
        this._range = range && range.length > 1 ? [range[0], range[range.length - 1]] : undefined;
        this.refresh();
        return this;
    }
    domain(domain) {
        this._domain =
            domain && domain.length > 1
                ? typeof domain[0] === 'object'
                    ? [domain[0].getTime(), domain[domain.length - 1].getTime()]
                    : [domain[0], domain[domain.length - 1]]
                : undefined;
        this.refresh();
        return this;
    }
    getDomain() {
        if (!this._domain) {
            const start = new Date().getTime() - 43200000;
            this._domain = [start, start + 86400000];
        }
        return this._domain;
    }
    getDomainAsDate() {
        const d = this.getDomain();
        return [new Date(d[0]), new Date(d[1])];
    }
    getRange() {
        if (!this._range) {
            this._range = [0, 100];
        }
        return this._range;
    }
    scale(scale) {
        const domain = scale.domain();
        const range = scale.range();
        this._domain =
            domain && domain.length > 1
                ? [domain[0].getTime(), domain[domain.length - 1].getTime()]
                : undefined;
        this._range = range && range.length > 1 ? [range[0], range[range.length - 1]] : undefined;
        return this;
    }
    fonts(minorFontProperties, majorFontProperties) {
        this.minorFontProperties = minorFontProperties;
        this.majorFontProperties = majorFontProperties;
        this.refreshTimelineScaleDefinitions();
        this.refresh();
        return this;
    }
    refresh() {
        const d = this.getDomain();
        const r = this.getRange();
        const tsd = this.getTimelineScaleDefinitions();
        this.scaleReference = d && r && tsd ? findTimelineScale(d, r, tsd) : undefined;
        return this;
    }
    getTimelineScaleDefinitions() {
        return this.timelineScaleDefinitions;
    }
    refreshTimelineScaleDefinitions() {
        this.timelineScaleDefinitions =
            this.minorFontProperties && this.majorFontProperties
                ? getTimelineScaleDefinitions(this.timeZone, this.locale, this.tickTextStartMargin, this.tickTextEndMargin, this.minorFontProperties, this.majorFontProperties)
                : undefined;
        return this.refresh();
    }
    isValid() {
        return !!this.timelineScaleDefinitions && !!this.scaleReference;
    }
    getDomainRangeRatio() {
        return this.scaleReference ? this.scaleReference.mspp : undefined;
    }
    getRangeInterval() {
        return this.scaleReference ? this.scaleReference.timeInterval : undefined;
    }
    getMajorRangeInterval() {
        return this.timelineScaleDefinitions && this.scaleReference
            ? this.timelineScaleDefinitions[scaleKeys[this.scaleReference.scaleIndex]].majorD3TimeInterval
            : undefined;
    }
    getDefinition() {
        return this.timelineScaleDefinitions && this.scaleReference
            ? this.timelineScaleDefinitions[scaleKeys[this.scaleReference.scaleIndex]]
            : undefined;
    }
    getScaleName() {
        return this.scaleReference ? this.scaleReference.intervalName : undefined;
    }
    getBaseIntervalMultiple() {
        return this.timelineScaleDefinitions && this.scaleReference
            ? this.timelineScaleDefinitions[scaleKeys[this.scaleReference.scaleIndex]].intervals[this.scaleReference.intervalIndex]
            : undefined;
    }
}
export const getTimelineScaleDefinitions = (timeZone, locale, tickTextStartMargin = 0, tickTextEndMargin = 0, minorFontProperties, majorFontProperties) => {
    const measureTimeZone = 'UTC';
    const time = new Date('2000-12-22T23:57:34.999Z');
    const measuredTimelineScaleDefinitions = scaleKeys
        .map((scaleKey) => {
        const definition = defaultTimelineScaleDefinitions[scaleKey];
        const measureFormatters = getIntlFormatters(definition, locale, measureTimeZone);
        const exampleMinor = measureFormatters.minor ? measureFormatters.minor(time) : '';
        const exampleMajor = measureFormatters.major ? measureFormatters.major(time) : '';
        const textMetrics = {
            minor: getTextMetrics(exampleMinor, minorFontProperties.font),
            major: getTextMetrics(exampleMajor, majorFontProperties.font),
            minorFont: minorFontProperties,
            majorFont: majorFontProperties
        };
        //console.log(`Font ${font} with scale ${scaleKey} has minor label ${exampleMinor} and width ${textMetrics.minor.width}`);
        const timelineScaleDefinition = (Object.assign({ textMetrics, formatters: getIntlFormatters(definition, locale, timeZone) }, definition));
        timelineScaleDefinition.minRangeSize =
            (textMetrics.minor ? Math.ceil(textMetrics.minor.width) : definition.minRangeSize) +
                tickTextStartMargin +
                tickTextEndMargin;
        return { key: scaleKey, value: timelineScaleDefinition };
    })
        .reduce((record, entry) => {
        record[entry.key] = entry.value;
        return record;
    }, {});
    return measuredTimelineScaleDefinitions;
};
export const getDateTimeFormatters = (locale, timeZone, timelineScaleKey = 'hours') => getIntlFormatters(defaultTimelineScaleDefinitions[timelineScaleKey], locale, timeZone);
const getIntlFormatters = (definition, locale, timeZone) => {
    const minor = definition.minor && Object.keys(definition.minor).length > 0
        ? Intl.DateTimeFormat(locale, { timeZone, ...definition.minor })
        : undefined;
    const major = definition.major && Object.keys(definition.major).length > 0
        ? Intl.DateTimeFormat(locale, { timeZone, ...definition.major })
        : undefined;
    const bound = definition.bound && Object.keys(definition.bound).length > 0
        ? Intl.DateTimeFormat(locale, { timeZone, ...definition.bound })
        : undefined;
    return {
        minor: minor
            ? (d) => minor.format(typeof d === 'number'
                ? new Date(d)
                : d.toISOString
                    ? d
                    : new Date(d.valueOf()))
            : (d) => '',
        major: major
            ? (d) => major.format(typeof d === 'number'
                ? new Date(d)
                : d.toISOString
                    ? d
                    : new Date(d.valueOf()))
            : (d) => '',
        bound: bound
            ? (d) => bound.format(typeof d === 'number'
                ? new Date(d)
                : d.toISOString
                    ? d
                    : new Date(d.valueOf()))
            : (d) => '',
        locale,
        timeZone
    };
};
export const getScaleDefinition = (scaleName, locale) => {
    const timeZone = 'UTC';
    const formatOptions = defaultTimelineScaleDefinitions[scaleName];
    let minorFormat = undefined;
    let majorFormat = undefined;
    if (formatOptions.minor && Object.keys(formatOptions.minor).length > 0) {
        minorFormat = Intl.DateTimeFormat(locale, { timeZone, ...formatOptions.minor });
    }
    if (formatOptions.major && Object.keys(formatOptions.major).length > 0) {
        majorFormat = Intl.DateTimeFormat(locale, { timeZone, ...formatOptions.major });
    }
    const now = new Date('2000-12-22T23:57:34.999Z');
    const exampleMinor = minorFormat ? minorFormat.format(now) : '';
    const exampleMajor = majorFormat ? majorFormat.format(now) : '';
    const def = {
        scale: scaleName,
        example: { minor: exampleMinor, major: exampleMajor },
        textMetrics: { minor: getTextMetrics(exampleMinor), major: getTextMetrics(exampleMajor) }
    };
    console.log(def);
};
export const getD3ScaleDefinition = (scaleName, locale) => {
    const defaultLocaleDefinition = getD3LocaleDefinition(locale);
    const defaultLocale = d3.timeFormatDefaultLocale(defaultLocaleDefinition);
    const formatOptions = defaultTimelineScaleDefinitions[scaleName];
    let minorFormat = '';
    let majorFormat = '';
    if (formatOptions.minor && Object.keys(formatOptions.minor).length > 0) {
        minorFormat = getD3FormatString(locale, formatOptions.minor, defaultLocaleDefinition);
    }
    if (formatOptions.major && Object.keys(formatOptions.major).length > 0) {
        majorFormat = getD3FormatString(locale, formatOptions.major, defaultLocaleDefinition);
    }
    console.log(`${scaleName} : { minor: ${minorFormat} , major: ${majorFormat} }`);
    const scaleMinorLocale = defaultLocale.format(minorFormat);
    const scaleMajorLocale = defaultLocale.format(majorFormat);
    const now = new Date('2000-12-22T23:57:34.999Z');
    const exampleMinor = scaleMinorLocale(now);
    const exampleMajor = scaleMajorLocale(now);
    const def = {
        scale: scaleName,
        example: { minor: exampleMinor, major: exampleMajor },
        textMetrics: { minor: getTextMetrics(exampleMinor), major: getTextMetrics(exampleMajor) }
    };
    console.log(def);
};
export const getD3LocaleDefinition = (locale) => {
    const periodLabels = getPeriodLabels(locale);
    const monthLabels = getMonthLabels(locale);
    const dayLabels = getDayLabels(locale);
    const definition = {
        dateTime: '',
        date: '',
        time: '',
        periods: periodLabels,
        days: dayLabels.long,
        shortDays: dayLabels.short,
        //	    narrowDays : dayLabels.narrow,
        months: monthLabels.long,
        shortMonths: monthLabels.short
        //	    narrowMonths : monthLabels.narrow
    };
    getD3DateTimeFormat(locale, definition);
    getD3DateFormat(locale, definition);
    getD3TimeFormat(locale, definition);
    return definition;
};
const getD3DateTimeFormat = (locale, definition) => {
    definition.dateTime = getD3FormatString(locale, { dateStyle: 'short', timeStyle: 'medium' }, definition);
    return definition;
};
const getD3DateFormat = (locale, definition) => {
    definition.date = getD3FormatString(locale, { dateStyle: 'short' }, definition);
    return definition;
};
const getD3TimeFormat = (locale, definition) => {
    definition.time = getD3FormatString(locale, { timeStyle: 'medium' }, definition);
    return definition;
};
const getD3FormatString = (locale, intFormatOptions, labels) => {
    const timeZone = 'UTC';
    const df = Intl.DateTimeFormat(locale, { timeZone, ...intFormatOptions });
    const date = new Date(Date.UTC(2000, 0, 2, 2, 1, 1, 1));
    const parts = df.formatToParts(date);
    const options = df.resolvedOptions();
    const formatString = parts.reduce((formatString, part) => {
        formatString += getDirective(part, options, labels);
        return formatString;
    }, '');
    return formatString;
};
const getDirective = (part, options, labels) => {
    //we expect the formatted date to be new Date(Date.UTC(2000, 0, 2,  2, 1,1,1) );
    let directive = '';
    if (part.type === 'literal') {
        directive = part.value;
    }
    else if (part.type === 'hour') {
        directive = options.hour12
            ? part.value.length == 1
                ? '%-I'
                : '%0I'
            : part.value.length == 1
                ? '%-H'
                : '%0H';
    }
    else if (part.type === 'minute') {
        directive = part.value.length == 1 ? '%M' : '%0M';
    }
    else if (part.type === 'second') {
        directive = part.value.length == 1 ? '%S' : '%0S';
    }
    else if (part.type === 'fractionalSecond') {
        directive = part.value.length <= 3 ? '%0L' : '%0f';
    }
    else if (part.type === 'dayPeriod') {
        directive = '%p';
    }
    else if (part.type === 'year') {
        directive = part.value.length <= 2 ? '%%0y' : '%Y';
    }
    else if (part.type === 'month') {
        directive = isNaN(part.value)
            ? getMonthLabelDirective(part.value, labels)
            : part.value.length == 1
                ? '%-m'
                : '%0m';
    }
    else if (part.type === 'day') {
        directive = part.value.length == 1 ? '%-d' : '%d';
    }
    else if (part.type === 'era') {
        //not supported by D3
    }
    else if (part.type === 'weekDay') {
        directive = isNaN(part.value) ? getWeekDayLabelDirective(part.value, labels) : '%w'; // '%u' for Monday-based (ISO 8601) weekday as a decimal number [1,7].
        directive = '%a';
    }
    else if (part.type === 'timeZone') {
        directive = '%Z';
    }
    return directive;
};
const getWeekDayLabelDirective = (value, labels) => {
    let directive = '';
    if (labels.days.indexOf(value) != -1) {
        directive = '%A';
    }
    else if (labels.shortDays.indexOf(value) != -1) {
        directive = '%a';
    }
    return directive;
};
const getMonthLabelDirective = (value, labels) => {
    let directive = '';
    if (labels.months.indexOf(value) != -1) {
        directive = '%B';
    }
    else if (labels.shortMonths.indexOf(value) != -1) {
        directive = '%b';
    }
    return directive;
};
const _defaultTimelineScaleDefinitions = {
    millis: {
        name: 'millis',
        minorD3BaseTimeInterval: d3.timeMillisecond,
        minorBaseIntervalMultiple: 1,
        majorD3TimeInterval: d3.timeSecond,
        intervals: [1, 2, 5, 10, 20, 25, 50, 100, 125, 200, 250, 500],
        minRangeSize: 40,
        minor: { fractionalSecondDigits: 3 },
        major: { hour: '2-digit', minute: '2-digit', second: '2-digit' },
        bound: { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 }
    },
    seconds: {
        name: 'seconds',
        minorD3BaseTimeInterval: d3.timeSecond,
        minorBaseIntervalMultiple: 1,
        majorD3TimeInterval: d3.timeMinute,
        intervals: [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30],
        minRangeSize: 40,
        minor: { second: '2-digit' },
        major: { hour: '2-digit', minute: '2-digit' },
        bound: { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 }
    },
    minutes: {
        name: 'minutes',
        minorD3BaseTimeInterval: d3.timeMinute,
        minorBaseIntervalMultiple: 1,
        majorD3TimeInterval: d3.timeHour,
        intervals: [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30],
        minRangeSize: 40,
        minor: { hour: '2-digit', minute: '2-digit' },
        major: { month: 'short', day: '2-digit', hour: '2-digit' },
        bound: {
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }
    },
    hours: {
        name: 'hours',
        minorD3BaseTimeInterval: d3.timeHour,
        minorBaseIntervalMultiple: 1,
        majorD3TimeInterval: d3.timeDay,
        intervals: [1, 2, 3, 4, 6, 8, 12],
        minRangeSize: 40,
        minor: { hour: '2-digit', minute: '2-digit' },
        major: { year: 'numeric', month: 'short', day: '2-digit' },
        bound: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
    },
    days: {
        name: 'days',
        minorD3BaseTimeInterval: d3.timeDay,
        minorBaseIntervalMultiple: 1,
        majorD3TimeInterval: d3.timeMonth,
        intervals: [1, 2, 3],
        minRangeSize: 40,
        minor: { day: '2-digit', month: 'short' },
        major: { year: 'numeric', month: 'short' },
        bound: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
    },
    weeks: {
        name: 'weeks',
        minorD3BaseTimeInterval: d3.timeWeek,
        minorBaseIntervalMultiple: 1,
        majorD3TimeInterval: d3.timeYear,
        intervals: [1, 2],
        minRangeSize: 40,
        minor: { month: 'short', day: '2-digit' },
        major: { year: 'numeric' },
        bound: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
    },
    months: {
        name: 'months',
        minorD3BaseTimeInterval: d3.timeMonth,
        minorBaseIntervalMultiple: 1,
        majorD3TimeInterval: d3.timeYear,
        intervals: [1, 2, 3, 4, 6],
        minRangeSize: 40,
        minor: { month: 'short' },
        major: { year: 'numeric' },
        bound: { year: 'numeric', month: 'short', day: '2-digit' }
    },
    years: {
        name: 'years',
        minorD3BaseTimeInterval: d3.timeYear,
        minorBaseIntervalMultiple: 1,
        majorD3TimeInterval: undefined,
        intervals: [1, 2, 5],
        minRangeSize: 40,
        minor: { year: 'numeric' },
        major: {},
        bound: { year: 'numeric', month: 'short' }
    },
    decades: {
        name: 'decades',
        minorD3BaseTimeInterval: d3.timeYear,
        minorBaseIntervalMultiple: 10,
        majorD3TimeInterval: undefined,
        intervals: [1, 2, 5],
        minRangeSize: 40,
        minor: { year: 'numeric' },
        major: {},
        bound: { year: 'numeric', month: 'short' }
    },
    centuries: {
        name: 'centuries',
        minorD3BaseTimeInterval: d3.timeYear,
        minorBaseIntervalMultiple: 100,
        majorD3TimeInterval: undefined,
        intervals: [1, 2, 5],
        minRangeSize: 40,
        minor: { year: 'numeric' },
        major: {},
        bound: { year: 'numeric' }
    },
    millennia: {
        name: 'millennia',
        minorD3BaseTimeInterval: d3.timeYear,
        minorBaseIntervalMultiple: 1000,
        majorD3TimeInterval: undefined,
        intervals: [1, 2, 5],
        minRangeSize: 60,
        minor: { year: 'numeric' },
        major: {},
        bound: { year: 'numeric' }
    },
    decamillennia: {
        name: 'decamillennia',
        minorD3BaseTimeInterval: d3.timeYear,
        minorBaseIntervalMultiple: 10000,
        majorD3TimeInterval: undefined,
        intervals: [1],
        minRangeSize: 60,
        minor: { year: 'numeric' },
        major: {},
        bound: { year: 'numeric' }
    }
};
const scaleKeys = [
    'millis',
    'seconds',
    'minutes',
    'hours',
    'days',
    'weeks',
    'months',
    'years',
    'decades',
    'centuries',
    'millennia',
    'decamillennia',
];
const defaultTimelineScaleDefinitions = _defaultTimelineScaleDefinitions;
const findTimelineScale = (domain, range, timelineScaleDefinitions) => {
    const domainSize = domain[1] - domain[0];
    const rangeSize = range[1] - range[0];
    const definitions = timelineScaleDefinitions
        ? timelineScaleDefinitions
        : defaultTimelineScaleDefinitions;
    const getRangeTickStep = (scaleIndex, intervalIndex, domainSize, rangeSize) => {
        let isValid = false;
        let chosenInterval = undefined;
        const definition = definitions[scaleKeys[scaleIndex]];
        const d3Interval = definition.minorD3BaseTimeInterval.every(definition.minorBaseIntervalMultiple * definition.intervals[intervalIndex]);
        if (d3Interval) {
            chosenInterval = d3Interval;
            const adjustedDomainStart = d3Interval?.floor(new Date(domain[0]));
            const adjustedDomainEnd = new Date(adjustedDomainStart.getTime() + domainSize);
            const intervalCount = definition.minorD3BaseTimeInterval.count(adjustedDomainStart, adjustedDomainEnd) /
                definition.minorBaseIntervalMultiple /
                definition.intervals[intervalIndex];
            const estimatedMinimumRangeSize = intervalCount * definition.minRangeSize;
            isValid =
                estimatedMinimumRangeSize <= rangeSize &&
                    estimatedMinimumRangeSize >= definition.minRangeSize;
            const estimatedTickStep = (definition.minRangeSize * rangeSize) / estimatedMinimumRangeSize;
            // console.info(
            // 	`Interval ${definition.name}_${
            // 		definition.intervals[intervalIndex]
            // 	} estimated min range: ${estimatedMinimumRangeSize}, est. TickStep: ${estimatedTickStep}, valid: ${
            // 		isValid ? 'OK' : ''
            // 	}`
            // );
        }
        else {
            console.warn(`d3.Timeinterval could not be constructed for ${definition.name}_definition.intervals[intervalIndex]`);
            isValid = false;
            chosenInterval = undefined;
        }
        const isLastOption = scaleIndex === scaleKeys.length - 1 && intervalIndex === definition.intervals.length - 1;
        return isValid || isLastOption ? chosenInterval : undefined;
    };
    let scaleIndex = 0;
    let intervalIndex = 0;
    let timeInterval = getRangeTickStep(scaleIndex, intervalIndex, domainSize, rangeSize);
    let intervalName = `${definitions[scaleKeys[scaleIndex]].name}_${definitions[scaleKeys[scaleIndex]].intervals[intervalIndex]}`;
    while (!timeInterval) {
        intervalIndex++;
        if (intervalIndex >= definitions[scaleKeys[scaleIndex]].intervals.length) {
            scaleIndex++;
            intervalIndex = 0;
        }
        timeInterval = getRangeTickStep(scaleIndex, intervalIndex, domainSize, rangeSize);
        intervalName = `${definitions[scaleKeys[scaleIndex]].name}_${definitions[scaleKeys[scaleIndex]].intervals[intervalIndex]}`;
    }
    return { scaleIndex, intervalIndex, timeInterval, intervalName, mspp: domainSize / rangeSize };
};
